import React, { useState, useRef, Fragment } from 'react';
import mainTheme from '../ProjectSettings.module.scss';
import theme from './ProjectSettingsPricingFields.module.scss';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ValidatingInput, ValidationForm, ValidationSummary } from '@/components/forms/validation';
import { useAsyncState } from '@/utils/hooks';
import { getPricingFields, savePricingFields } from '@/services/projects';
import { Button, ButtonBar } from '@ygtechnologies/rubus-ui';
import { faPlus, faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import { classnames } from '@/utils/html';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from '@/components/Tooltip';
import { PricingField, OptionalId } from '@/types';
import YesNoDialog from '@/components/dialogs/YesNoDialog';
import InfoIconWithTooltip from '@/seller/components/InfoIconWithTooltip';
import { Toast } from '@ygtechnologies/bygr-frontend-component-library';

type Props = {
    // OwnProps
    projectId: number;
};

const ProjectSettingsPricingFields: React.FC<Props> = ({ projectId }) => {
    const btnRef = useRef<HTMLButtonElement>(null);
    const [pricingFields, setPricingFields] = useAsyncState<OptionalId<PricingField>[]>(
        () => getPricingFields(projectId),
        [],
        []
    );
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isDialogVisible, setIsDialogVisible] = useState(false);
    if (pricingFields === null) {
        return null;
    }

    const onSubmit = () => {
        if (isDeleting) {
            setIsDialogVisible(true);
        } else {
            onSave();
        }
    };

    const onSave = async () => {
        try {
            setIsSaving(true);
            const newPf = await savePricingFields(projectId, pricingFields);
            setPricingFields(newPf);
            Toast.success(<Trans>Saved pricing fields</Trans>);
        } finally {
            setIsDeleting(false);
            setIsSaving(false);
        }
    };
    return (
        <>
            <ValidationForm onSubmit={onSubmit} className={mainTheme.section}>
                <div className={theme.gridContainer}>
                    <h5>
                        <Trans>Variable name</Trans>
                        <InfoIconWithTooltip
                            content={
                                <Trans>
                                    This is the name of the pricing variable. Keep it short and simple. Use the description
                                    to elaborate what this variable should be used for.
                                </Trans>
                            }
                        />
                    </h5>
                    <h5>
                        <Trans>Variable description</Trans>
                        <InfoIconWithTooltip
                            content={
                                <Trans>
                                    Adding a description will help subcontractors and vendors understand how to use this
                                    pricing variable.
                                </Trans>
                            }
                        />
                    </h5>
                    <h5>
                        <Trans>Unit of measure</Trans>
                        <InfoIconWithTooltip
                            content={
                                <Trans>
                                    Use this field to indicate which unit of measure this variable represents. For example,
                                    for flooring use ‘sqm’. This value will be shown to the buyers so that they better
                                    understand how certain options are priced.
                                </Trans>
                            }
                        />
                    </h5>
                    <span />
                    {pricingFields.map((pf, index) => (
                        <Fragment key={index}>
                            <ValidatingInput
                                maxLength={50}
                                type="text"
                                required
                                className={'form-control'}
                                placeholder={t`Variable name`}
                                value={pf.name || ''}
                                onChange={(e) =>
                                    setPricingFields(
                                        pricingFields.map((_pf, i) => (i === index ? { ..._pf, name: e.target.value } : _pf))
                                    )
                                }
                            />
                            <ValidatingInput
                                maxLength={100}
                                type="text"
                                className={'form-control'}
                                placeholder={t`Description`}
                                value={pf.description || ''}
                                onChange={(e) =>
                                    setPricingFields(
                                        pricingFields.map((_pf, i) =>
                                            i === index ? { ..._pf, description: e.target.value } : _pf
                                        )
                                    )
                                }
                            />
                            <ValidatingInput
                                maxLength={50}
                                type="text"
                                className={'form-control'}
                                placeholder={t`MeasurementUnitShort`}
                                value={pf.unit || ''}
                                onChange={(e) =>
                                    setPricingFields(
                                        pricingFields.map((_pf, i) => (i === index ? { ..._pf, unit: e.target.value } : _pf))
                                    )
                                }
                            />
                            <Tooltip
                                content={
                                    pf.isDeletable ? null : <Trans>Can not be deleted. This pricing field is in use.</Trans>
                                }>
                                <span className={classnames(mainTheme.buttonWrapper, theme.deleteButton)}>
                                    <Button
                                        variant="danger"
                                        disabled={!pf.isDeletable}
                                        className={mainTheme.trashButton}
                                        onClick={() => {
                                            setPricingFields(pricingFields.filter((e, i) => i !== index));
                                            setIsDeleting(true);
                                        }}>
                                        <FontAwesomeIcon size="lg" icon={faTrashAlt} />
                                    </Button>
                                </span>
                            </Tooltip>
                        </Fragment>
                    ))}
                </div>

                <ValidationSummary />
                <ButtonBar>
                    <Button
                        icon={faPlus}
                        variant="tertiary"
                        onClick={() =>
                            setPricingFields(
                                pricingFields.concat([{ name: '', description: '', isDeletable: true, unit: '' }])
                            )
                        }>
                        <Trans>Add variable</Trans>
                    </Button>
                    <ButtonBar.Right>
                        <Button
                            ref={btnRef}
                            className={mainTheme.saveButton}
                            type="submit"
                            variant="primary"
                            loading={isSaving}>
                            <Trans>Save</Trans>
                        </Button>
                    </ButtonBar.Right>
                </ButtonBar>
            </ValidationForm>
            <YesNoDialog
                show={isDialogVisible}
                onClose={() => setIsDialogVisible(false)}
                body={<Trans>Are you sure you want to delete the pricing variable?</Trans>}
                title={<Trans>Are you sure?</Trans>}
                onAccept={onSave}
            />
        </>
    );
};

export default ProjectSettingsPricingFields;
